import { isValid, parseISO } from "date-fns";
import type { WizardProfileAddress } from "../types";

/**
 * agentExtractedData delivers dates as raw strings. Normalize ISO-8601 values into a real Date
 * so date pickers and toISOFormat never receive an unparseable string. Non-ISO strings become
 * undefined (left for the user to fill) rather than throwing or being guessed at ambiguously.
 */
export const parseExtractedDate = (value: unknown): Date | undefined => {
  if (value instanceof Date) return isValid(value) ? value : undefined;
  if (typeof value !== "string" || value.trim() === "") return undefined;

  const isoDate = parseISO(value);
  return isValid(isoDate) ? isoDate : undefined;
};

export const mergeExtractedData = <T extends Record<string, unknown>>(
  initialValues: T,
  fieldMapping: Partial<Record<keyof T, string>>,
  extractedData?: Record<string, string> | null,
): T => {
  if (!extractedData) return initialValues;

  const merged: T = { ...initialValues };

  (Object.keys(initialValues) as Array<keyof T>).forEach((fieldKey) => {
    const extractedKey = fieldMapping[fieldKey];
    const extractedValue = extractedKey ? extractedData[extractedKey] : undefined;
    if (extractedValue !== undefined && extractedValue !== null && extractedValue !== "") {
      (merged as Record<keyof T, unknown>)[fieldKey] = extractedValue;
    }
  });

  return merged;
};

type AddressFormValues = {
  streetLine1?: string;
  streetLine2?: string;
  city?: string;
  stateOrProvince?: string;
  country?: string;
  zipCode?: string;
};

export const toWizardAddress = (values: AddressFormValues): WizardProfileAddress => ({
  streetLine1: values.streetLine1,
  streetLine2: values.streetLine2,
  city: values.city,
  stateOrProvince: values.stateOrProvince,
  country: values.country,
  zipCode: values.zipCode,
});
